// PullToRefresh — wraps a scrollable region and adds an iOS-style pull-to-
// refresh gesture. Only activates when the user is already scrolled to the
// top, mirrors Apple's spring resistance, and triggers a hard reload (which
// also asks the service worker to fetch fresh JS bundles).
//
// We deliberately keep the indicator minimal: a single chevron that fades in
// + rotates as the user pulls past the threshold. No spinning circles, no
// dropdown bars — this is a quiet utility, not a feature.

import { useEffect, useRef, useState } from "react";
import { ChevronDown, RefreshCw } from "lucide-react";

const THRESHOLD = 70;       // px — pull distance required to trigger refresh
const MAX_PULL  = 110;      // px — visual ceiling, gestures past this stop pulling
const RESISTANCE = 0.5;     // 1 = no resistance, lower = more iOS-like drag

async function hardRefresh() {
    // Ask any registered service workers to look for a new bundle.  Even if
    // they're identical, .update() forces them to re-check the network.
    try {
        if ("serviceWorker" in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.update().catch(() => null)));
        }
    } catch {
        /* noop — best-effort */
    }
    window.location.reload();
}

export default function PullToRefresh({ children }) {
    const startY = useRef(null);
    const pullingRef = useRef(false);
    const [pullY, setPullY] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        const onTouchStart = (e) => {
            // Only allow the gesture to begin when the page is scrolled to the
            // very top.  Otherwise normal scroll wins.
            if (window.scrollY > 0) return;
            startY.current = e.touches[0].clientY;
            pullingRef.current = false;
        };
        const onTouchMove = (e) => {
            if (startY.current == null) return;
            const dy = e.touches[0].clientY - startY.current;
            if (dy <= 0) {
                setPullY(0);
                pullingRef.current = false;
                return;
            }
            // Only intercept when we're at top AND user is dragging downwards.
            if (window.scrollY > 0) {
                startY.current = null;
                setPullY(0);
                pullingRef.current = false;
                return;
            }
            pullingRef.current = true;
            // Apply spring resistance so the pull feels heavier the further
            // the user drags — exactly like iOS native scroll bounce.
            const eased = Math.min(MAX_PULL, dy * RESISTANCE);
            setPullY(eased);
        };
        const onTouchEnd = () => {
            if (!pullingRef.current) {
                setPullY(0);
                startY.current = null;
                return;
            }
            startY.current = null;
            pullingRef.current = false;
            if (pullY >= THRESHOLD) {
                setRefreshing(true);
                // Snap the indicator to its committed position briefly so the
                // user sees the action "land" before the reload.
                setPullY(THRESHOLD);
                setTimeout(hardRefresh, 250);
            } else {
                setPullY(0);
            }
        };

        // passive: false is required so we can prevent the page from
        // rubber-banding when we're handling the gesture ourselves.
        document.addEventListener("touchstart", onTouchStart, { passive: true });
        document.addEventListener("touchmove",  onTouchMove,  { passive: true });
        document.addEventListener("touchend",   onTouchEnd,   { passive: true });
        document.addEventListener("touchcancel", onTouchEnd,  { passive: true });
        return () => {
            document.removeEventListener("touchstart", onTouchStart);
            document.removeEventListener("touchmove",  onTouchMove);
            document.removeEventListener("touchend",   onTouchEnd);
            document.removeEventListener("touchcancel", onTouchEnd);
        };
    }, [pullY]);

    const past = pullY >= THRESHOLD;
    const progress = Math.min(1, pullY / THRESHOLD);

    return (
        <>
            {/* Refresh indicator — single chevron / spinning icon. Lives at the
                very top of the viewport; appears + rotates as the user pulls. */}
            <div
                className="fixed left-0 right-0 z-40 flex justify-center pointer-events-none"
                style={{
                    top: "max(env(safe-area-inset-top), 8px)",
                    transform: `translateY(${pullY * 0.4}px)`,
                    opacity: progress,
                    transition: pullY === 0 ? "transform 250ms ease-out, opacity 200ms ease-out" : "none",
                }}
                aria-hidden
                data-testid="ptr-indicator"
            >
                <div
                    className="h-9 w-9 rounded-full bg-card/90 border border-border/60 backdrop-blur-md flex items-center justify-center"
                    style={{
                        boxShadow: "0 6px 24px rgba(0,0,0,0.35)",
                    }}
                >
                    {refreshing ? (
                        <RefreshCw size={15} className="text-foreground animate-spin" />
                    ) : (
                        <ChevronDown
                            size={16}
                            className={past ? "text-[#A855F7]" : "text-muted-foreground"}
                            style={{
                                transform: `rotate(${past ? 180 : progress * 180}deg)`,
                                transition: "transform 200ms ease-out, color 200ms ease-out",
                            }}
                        />
                    )}
                </div>
            </div>

            {/* Content — translates with the pull so it feels physically
                connected.  Spring back when released.  When idle (`pullY===0`)
                we omit the transform entirely so we don't create a CSS
                containing block for fixed-positioned descendant modals
                (Future You, GaugeTile insight modal, etc.). */}
            <div
                style={{
                    transform: pullY > 0 ? `translateY(${pullY}px)` : undefined,
                    transition: pullY === 0 ? "transform 280ms cubic-bezier(0.2, 0.9, 0.3, 1)" : "none",
                    willChange: pullY > 0 ? "transform" : undefined,
                }}
            >
                {children}
            </div>
        </>
    );
}
